import { View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import {
  PMSmallNumber,
  PMSmallNumberCardEditSchema,
} from "@pimo/pimo-components/src/lib/pm-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { APP_RECORD_TYPES, PROJECT_NAMES } from "vmd-types";

import { TestAppState } from "../../../app";
import {
  criticalityLevelSet,
  getAllReportingItems,
} from "../../../helpers/pages-helpers/dashboard-plugin-helpers";
import {
  countReportingItemsByRecordType,
  isRecordTypeVisible,
} from "../../../utils/filter-reporting-items-by-record-type";

const getProjectsCount = (
  dashboard: TestAppState["dashboard"] | undefined
): number => {
  return (dashboard?.projects ?? []).length;
};

/**
 * The four small-number "count" cards that sit between the criticality
 * charts and the distribution-channel charts: projects, business
 * applications, (conditionally) IT services, and critical DC Business Apps
 * (#2222).
 */
export function addCountsCard(
  view: View<TestAppState, DragAndDropLayout>
): void {
  const numberOfProjectsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "numberOfProjectsComponent",
      labels: ["#ofProjects"],
      description: "Shows the number of projects.",
      title: "Projects",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  numberOfProjectsComponent.mapState(({ dashboard, settings, language }) => ({
    showInfoIcon: false,
    showMenuIcon: false,
    title:
      PROJECT_NAMES.find(
        (name) => name.single === settings?.configuration?.projectName
      )?.plural ??
      getLocalizedMessage(language || "en", "projects") ??
      "Projects",
    number: getProjectsCount(dashboard),
  }));

  const numberOfBusinessApplicationsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "numberOfBusinessApplicationsDashboardComponent",
      labels: ["#ofBusinessApplications"],
      description: "Shows the number of DC Business Apps across all projects.",
      title: "DC Business Apps",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  numberOfBusinessApplicationsComponent.mapState(({ dashboard }) => {
    const allItems = getAllReportingItems(dashboard);
    return {
      title: "DC Business Apps",
      number: countReportingItemsByRecordType(
        allItems,
        APP_RECORD_TYPES.BUSINESS_APPLICATION
      ),
    };
  });

  if (isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)) {
    const numberOfITServicesComponent = view.addComponent({
      component: PMSmallNumber,
      layoutProps: {
        componentId: "numberOfITServicesDashboardComponent",
        labels: ["#ofITServices"],
        description: "Shows the number of IT Services across all projects.",
        title: "IT Services",
        editSchema: PMSmallNumberCardEditSchema,
      },
    });

    numberOfITServicesComponent.mapState(({ dashboard }) => {
      const allItems = getAllReportingItems(dashboard);
      return {
        title: "IT Services",
        number: countReportingItemsByRecordType(
          allItems,
          APP_RECORD_TYPES.IT_SERVICE
        ),
      };
    });
  }

  const criticalDCSystemsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "criticalDCSystemsDashboardComponent",
      labels: ["#criticalDCSystems"],
      description:
        "Shows the number of critical DC Business Apps across all projects.",
      title: "Critical DC Business Apps",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  criticalDCSystemsComponent.mapState(({ dashboard }) => {
    const allItems = getAllReportingItems(dashboard);
    const criticalCount = allItems.filter(
      (item) =>
        !!item?.basicItem?.criticality &&
        criticalityLevelSet.has(item.basicItem.criticality)
    ).length;

    return {
      title: "Critical DC Business Apps",
      number: criticalCount,
    };
  });
}
